import { container } from '@shared/common/dependency-injection/domain/container';
import { loadContainer } from '@client/composition/core/application/helper/load-client-container.helper';
import 'reflect-metadata';

import '@shared/assets/main.css';

import { createApp } from 'vue';
import '@kiplin/web-common/design-system/typography.css';
import '@kiplin/web-common/design-system/app-colors.css';
import '@kiplin/web-common/design-system/design-system.css';
import '@kiplin/web-common/toast/toast.css';

import { DIDependencyTypes } from '@shared/common/dependency-injection/di.dependency-types';
import router from '@client/composition/router/application/client.router';
import { useSentry } from '@kiplin/web-common/error-monitoring';
import { createPinia } from 'pinia';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { ClientI18nVuePlugin } from '@client/composition/core/application/vue-plugin/client-i18n.vue-plugin';
import type { AppProps } from '@client/ClientApp.types';
import App from '@client/ClientApp.vue';
import { loadHostContainerModule } from '@shared/common/host/application/service/host-container-module-loader.service';
import { MissingAppMountError, MissingAppPropError } from '@shared/common/app/domain/error/app-bootstrap.error';
import { loadHelpCenterContainerModule } from '@client/feature/help-center/application/service/help-center-container-module-loader.service';
import { FontAwesomeVuePlugin } from '@kiplin/web-common/icon';
import { TrackingVuePlugin } from '@shared/common/tracking/application/vue-plugin/tracking.vue-plugin';
import { loadTrackingContainerModule } from '@shared/common/tracking/application/service/tracking-container-module-loader.service';
import { NETWORK_COCKPIT_ERROR_NAME } from '@shared/common/error/domain/error/network-cockpit.error';
import { RouterVuePlugin } from '@kiplin/web-common/navigation';

const pinia = createPinia();
const mountEl = document.querySelector('#app') as HTMLElement | null;

if (!mountEl) {
  throw new MissingAppMountError();
}

const buildAppProps = (dataset: DOMStringMap): AppProps => {
  const getRequired = (key: keyof AppProps): string => {
    const value = dataset[key as keyof DOMStringMap];
    if (!value) {
      throw new MissingAppPropError(String(key));
    }
    return value;
  };

  return {
    appEnvironment: dataset.appEnvironment,
    sentryEnvironment: dataset.sentryEnvironment,
    userLocale: getRequired('userLocale'),
    apiToken: dataset.apiToken,
    helpUrl: dataset.helpUrl,
    hubHost: getRequired('hubHost'),
    adventureHost: getRequired('adventureHost'),
    challengeHost: getRequired('challengeHost'),
    racingHost: getRequired('racingHost'),
    kwizHost: getRequired('kwizHost'),
    messagingHost: getRequired('messagingHost'),
    posthogKey: dataset.posthogKey,
    posthogApiHost: dataset.posthogApiHost,
    posthogUiHost: dataset.posthogUiHost,
  };
};

const appProps = buildAppProps(mountEl.dataset);

loadTrackingContainerModule(appProps);
loadHostContainerModule(appProps);
loadHelpCenterContainerModule(appProps);

loadContainer().then(() => {
  const app = createApp(App, { ...appProps }); // Pass App like this instead of createApp(App) to be able to pass props from backend

  const sentry = useSentry();
  sentry.init({
    app,
    router,
    appEnvironment: appProps.appEnvironment ?? 'undefined',
    sentryEnvironment: appProps.sentryEnvironment ?? 'undefined',
    projectName: 'cockpit',
    dsn: 'https://e0f3d5d9e6fc2c0683814b261ec5dd0b@o4507073724743680.ingest.de.sentry.io/4510307111731280',
    ignoreErrors: [
      /Request failed with status code 401/,
      /Cockpit API request failed with HTTP 401/,
      NETWORK_COCKPIT_ERROR_NAME,
    ],
    denyUrls: [/toucantoco\.com/i],
  });

  app.use(FontAwesomeVuePlugin, { iconKitSource: 'package' });
  app.use(ClientI18nVuePlugin);
  app.use(VueDOMPurifyHTML, {
    default: {
      // eslint-disable-next-line no-useless-escape
      ALLOWED_URI_REGEXP:
        /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|xxx|https?-ext):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    },
  });

  app.use(pinia);
  app.use(TrackingVuePlugin);

  app.provide(DIDependencyTypes.Container, container);
  app.provide('app', app);

  app.use(RouterVuePlugin, router);
  app.mount('#app');
});
